import { Component, computed, input, model } from "@angular/core";
import { EChartsOption } from "echarts/types/dist/shared";

import { SegySpectrumResponse } from "../../../../models/segy";

export type SpectrumScale = "linear" | "log" | "db";

/** Hard axis limits for the spectrum; null bounds are automatic. Kept by the
 * caller so the same frame is reused for every gather (and raw ↔ filtered). */
export interface SpectrumLimits {
  scale: SpectrumScale;
  fMin: number | null;
  fMax: number | null;
  aMin: number | null;
  aMax: number | null;
}

export const DEFAULT_SPECTRUM_LIMITS: SpectrumLimits = {
  scale: "db",
  fMin: null,
  fMax: null,
  aMin: null,
  aMax: null,
};

/**
 * Average amplitude spectrum of the displayed gather (raw + filtered) with
 * user-set, persistent axis limits: frequency and amplitude bounds stay fixed
 * as you step between gathers or flip raw ↔ filtered, so views are directly
 * comparable and reproducible. "Lock view" freezes whatever is on screen.
 */
@Component({
  selector: "app-segy-spectrum-chart",
  templateUrl: "./spectrum-chart.component.html",
  styleUrl: "./spectrum-chart.component.less",
  standalone: false,
})
export class SegySpectrumChartComponent {
  public readonly spectrum = input<SegySpectrumResponse | null>(null);
  public readonly loading = input<boolean>(false);
  public readonly limits = model<SpectrumLimits>(DEFAULT_SPECTRUM_LIMITS);
  /** Nyquist (Hz), shown as the automatic upper frequency bound. */
  public readonly nyquist = input<number>(0);

  protected readonly scales: { value: SpectrumScale; label: string }[] = [
    { value: "db", label: "dB" },
    { value: "linear", label: "Lin" },
    { value: "log", label: "Log" },
  ];

  private chart: any = null;

  protected readonly ampUnit = computed(() =>
    this.limits().scale === "db" ? "dB" : "amp",
  );

  protected readonly hasLimits = computed(() => {
    const l = this.limits();
    return [l.fMin, l.fMax, l.aMin, l.aMax].some((v) => v != null);
  });

  protected readonly options = computed<EChartsOption | null>(() =>
    this.buildOptions(),
  );

  protected setLimit(key: keyof Omit<SpectrumLimits, "scale">, value: any) {
    this.limits.update((l) => ({ ...l, [key]: value ?? null }));
  }

  protected setScale(scale: SpectrumScale) {
    // amplitude bounds are in the scale's units, so they don't carry over
    this.limits.update((l) => ({ ...l, scale, aMin: null, aMax: null }));
  }

  protected resetLimits() {
    this.limits.update((l) => ({ ...DEFAULT_SPECTRUM_LIMITS, scale: l.scale }));
  }

  protected onChartInit(chart: any) {
    this.chart = chart;
  }

  /** Freeze the current on-screen (zoomed) extent as hard limits. */
  protected lockView() {
    const chart = this.chart;
    const rect = chart
      ?.getModel?.()
      ?.getComponent("grid", 0)
      ?.coordinateSystem?.getRect?.();
    if (!rect) return;
    // read the plot corners back as data values: this is right for every axis
    // type (a log axis' internal extent is not in data units)
    const lo = chart.convertFromPixel({ gridIndex: 0 }, [
      rect.x,
      rect.y + rect.height,
    ]);
    const hi = chart.convertFromPixel({ gridIndex: 0 }, [
      rect.x + rect.width,
      rect.y,
    ]);
    if (!lo || !hi || ![...lo, ...hi].every((v: number) => isFinite(v))) return;
    const round = (v: number) => Number(v.toPrecision(4));
    this.limits.update((l) => ({
      ...l,
      fMin: round(Math.max(0, lo[0])),
      fMax: round(hi[0]),
      aMin: round(lo[1]),
      aMax: round(hi[1]),
    }));
  }

  private buildOptions(): EChartsOption | null {
    const spectrum = this.spectrum();
    if (!spectrum?.freq?.length) return null;
    const limits = this.limits();
    const scale = limits.scale;
    // dB is relative to the raw peak, so the filtered curve shows attenuation
    const ref = Math.max(...spectrum.raw.filter((v) => v > 0), 0) || 1;
    const transform = (v: number | null | undefined): number | null => {
      if (v == null || !isFinite(v)) return null;
      if (scale === "db") return v > 0 ? 20 * Math.log10(v / ref) : null;
      if (scale === "log") return v > 0 ? v : null;
      return v;
    };
    const pairs = (amp: number[]) =>
      spectrum.freq.map((f, i) => [f, transform(amp[i])]);

    const series: any[] = [
      {
        name: "Raw",
        type: "line",
        showSymbol: false,
        sampling: "lttb",
        animation: false,
        lineStyle: { width: 1.5 },
        color: "#8c8c8c",
        data: pairs(spectrum.raw),
      },
    ];
    if (spectrum.filtered?.length) {
      series.push({
        name: "Filtered",
        type: "line",
        showSymbol: false,
        sampling: "lttb",
        animation: false,
        lineStyle: { width: 1.5 },
        color: "#1890ff",
        data: pairs(spectrum.filtered),
      });
    }

    const fMaxAuto = spectrum.freq[spectrum.freq.length - 1];
    const fmt = (v: number) =>
      scale === "db"
        ? `${Number(v.toFixed(1))}`
        : Math.abs(v) >= 1000 || (Math.abs(v) < 0.01 && v !== 0)
          ? v.toExponential(1)
          : `${Number(v.toPrecision(3))}`;

    return {
      animation: false,
      legend: { top: 0, data: series.map((s) => s.name) },
      grid: { left: 8, right: 16, top: 28, bottom: 30, containLabel: true },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross", animation: false },
        valueFormatter: (val: any) =>
          val == null ? "-" : `${fmt(Number(val))} ${this.ampUnit()}`,
      },
      xAxis: {
        type: "value",
        name: "Frequency (Hz)",
        nameLocation: "middle",
        nameGap: 24,
        min: limits.fMin ?? 0,
        max: limits.fMax ?? fMaxAuto,
        axisLabel: { formatter: (v: number) => `${Number(v.toFixed(1))}` },
      },
      yAxis: {
        type: scale === "log" ? "log" : "value",
        name: scale === "db" ? "Amplitude (dB re raw peak)" : "Amplitude",
        nameLocation: "middle",
        nameGap: 48,
        min: limits.aMin ?? undefined,
        max: limits.aMax ?? (scale === "db" ? 0 : undefined),
        axisLabel: { formatter: (v: number) => fmt(v) },
      },
      // inside zoom only: limits come from the inputs (or "Lock view")
      dataZoom: [
        {
          type: "inside",
          xAxisIndex: 0,
          filterMode: "none",
        },
      ],
      series,
    } as EChartsOption;
  }
}
